import useRequest from "@/lib/request/useRequest";
import { FieldConnection } from "@/types/connection";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    DateRangePicker,
    Card,
    CardBody,
    Checkbox,
    Divider,
    RangeValue,
    DateValue,
    ButtonGroup,
} from "@heroui/react";
import { useState, useMemo, useEffect } from "react";
import dayjs from "dayjs";
import johnDeereLogo from "@/assets/images/JDOC-tm-default.svg";
import { Platform } from "@/types/enums";

type JohnDeereOperation = {
    id: string;
    startDate: string;
    type: string;
    cropName: string;
    products: {
        name: string;
    }[];
};

type OperationGroup = {
    id: string;
    operations: JohnDeereOperation[];
};

function clusterOperationsByTypeAndDate(
    operations: JohnDeereOperation[],
): OperationGroup[] {
    const byType = new Map<string, JohnDeereOperation[]>();
    for (const op of operations) {
        const key = op.type.toLowerCase();
        const list = byType.get(key);
        if (list) list.push(op);
        else byType.set(key, [op]);
    }

    const groups: OperationGroup[] = [];

    for (const ops of byType.values()) {
        const sorted = [...ops].sort(
            (a, b) =>
                dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf(),
        );
        let bucket: JohnDeereOperation[] = [];
        let groupMin = dayjs(sorted[0].startDate);
        let groupMax = groupMin;

        const flush = () => {
            if (bucket.length === 0) return;
            const ids = [...new Set(bucket.map((o) => o.id))].sort().join(",");
            groups.push({ id: ids, operations: bucket });
            bucket = [];
        };

        for (const op of sorted) {
            const d = dayjs(op.startDate);
            if (bucket.length === 0) {
                bucket = [op];
                groupMin = d;
                groupMax = d;
                continue;
            }
            const nextMin = d.isBefore(groupMin) ? d : groupMin;
            const nextMax = d.isAfter(groupMax) ? d : groupMax;
            if (nextMax.diff(nextMin, "day") <= 3) {
                bucket.push(op);
                groupMin = nextMin;
                groupMax = nextMax;
            } else {
                flush();
                bucket = [op];
                groupMin = d;
                groupMax = d;
            }
        }
        flush();
    }

    return groups.sort(
        (a, b) =>
            dayjs(b.operations[0].startDate).valueOf() -
            dayjs(a.operations[0].startDate).valueOf(),
    );
}

function ImportOperation({
    connection,
    onClose,
}: {
    connection: FieldConnection;
    onClose: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const importOperationMutation = useMutateRequest({
        method: "post",
        url: `field_connections/${connection?.id}/operations/`,
        successMessage: "Operations imported successfully",
        errorMessage: "Failed to import operations",
        onSuccess: () => {
            onClose();

        },
    });
    const operationsQuery = useGetRequest<JohnDeereOperation[]>({
        url: `field_connections/${connection?.id}/operations/`,
        enabled: !!connection?.id,
    });
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(null);
    const [selectedType, setSelectedType] = useState<string>("all");

    const filteredOperations = useMemo(() => {
        if (!operationsQuery.data) return undefined;
        return operationsQuery.data.filter((operation) => {
            const opType = (operation.type ?? "").toLowerCase();
            if (selectedType !== "all" && opType !== selectedType) {
                return false;
            }
            if (!dateRange?.start || !dateRange?.end) {
                return true;
            }
            const opDay = dayjs(operation.startDate).format("YYYY-MM-DD");
            const startDay = dayjs(dateRange.start.toString()).format("YYYY-MM-DD");
            const endDay = dayjs(dateRange.end.toString()).format("YYYY-MM-DD");
            return opDay >= startDay && opDay <= endDay;
        });
    }, [operationsQuery.data, selectedType, dateRange]);

    const operationGroups = useMemo(
        () => clusterOperationsByTypeAndDate(filteredOperations ?? []),
        [filteredOperations],
    );

    /** One entry per selected UI group: `{ type, ids }` for the API. */
    const operationsImportPayload = useMemo(() => {
        const byId = new Map(operationGroups.map((g) => [g.id, g]));
        return selectedGroupIds.flatMap((gid) => {
            const g = byId.get(gid);
            if (!g?.operations.length) return [];
            return [
                {
                    type: g.operations[0].type,
                    ids: g.operations.map((o) => o.id),
                },
            ];
        });
    }, [operationGroups, selectedGroupIds]);

    useEffect(() => {
        const valid = new Set(operationGroups.map((g) => g.id));
        setSelectedGroupIds((prev) => prev.filter((id) => valid.has(id)));
    }, [operationGroups]);

    return (
        <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside" className="h-[60vh]">
            <ModalContent>
                <ModalHeader className="flex flex-row gap-2 items-center">
                    <p className="text-lg">Import Operation</p>{" "}
                    {connection.user_connection.platform ===
                        Platform.JohnDeere && (
                            <img
                                src={johnDeereLogo}
                                alt="John Deere"
                                className="h-12 w-auto object-contain"
                            />
                        )}
                </ModalHeader>
                <ModalBody className="flex flex-col gap-2 mx-1">
                    <div className="flex flex-row gap-4">
                        <Checkbox
                            isSelected={
                                operationGroups.length > 0 &&
                                selectedGroupIds.length ===
                                operationGroups.length
                            }
                            onValueChange={() => {
                                setSelectedGroupIds(
                                    selectedGroupIds.length ===
                                        operationGroups.length
                                        ? []
                                        : operationGroups.map((g) => g.id),
                                );
                            }}
                        />
                        <DateRangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            showMonthAndYearPickers
                        />
                        <ButtonGroup>
                            <Button
                                color={
                                    selectedType === "all"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("all")}
                            >
                                All
                            </Button>
                            <Button
                                color={
                                    selectedType === "planting"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("planting")}
                            >
                                Planting
                            </Button>
                            <Button
                                color={
                                    selectedType === "application"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("application")}
                            >
                                Application
                            </Button>
                            <Button
                                color={
                                    selectedType === "harvest"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("harvest")}
                            >
                                Harvest
                            </Button>
                        </ButtonGroup>
                    </div>
                    <Divider />
                    {operationsQuery.isLoading && <Spinner />}
                    {!operationsQuery.isLoading &&
                        operationGroups.length === 0 && (
                            <div className="flex flex-col gap-2">
                                <p className="text-center text-gray-500">
                                    No operations found
                                </p>
                            </div>
                        )}
                    <div className="flex flex-col gap-2">
                        {operationGroups.map((group) => {
                            const first = group.operations[0];
                            const dates = group.operations.map((o) =>
                                dayjs(o.startDate),
                            );
                            const minD = dates.reduce((a, b) =>
                                a.isBefore(b) ? a : b,
                            );
                            const maxD = dates.reduce((a, b) =>
                                a.isAfter(b) ? a : b,
                            );
                            const dateLabel =
                                minD.isSame(maxD, "day")
                                    ? minD.format("YYYY-MM-DD")
                                    : `${minD.format("YYYY-MM-DD")} – ${maxD.format("YYYY-MM-DD")}`;
                            const detail =
                                first.type === "Application"
                                    ? [
                                        ...new Set(
                                            group.operations.flatMap((o) =>
                                                (o.products ?? []).map(
                                                    (p) => p.name,
                                                ),
                                            ),
                                        ),
                                    ].join(", ")
                                    : [
                                        ...new Set(
                                            group.operations.map(
                                                (o) => o.cropName,
                                            ),
                                        ),
                                    ].join(", ");
                            return (
                                <Card key={group.id}>
                                    <CardBody className="flex flex-row gap-2">
                                        <Checkbox
                                            isSelected={selectedGroupIds.includes(
                                                group.id,
                                            )}
                                            onValueChange={() => {
                                                setSelectedGroupIds((prev) =>
                                                    prev.includes(group.id)
                                                        ? prev.filter(
                                                            (id) =>
                                                                id !==
                                                                group.id,
                                                        )
                                                        : [...prev, group.id],
                                                );
                                            }}
                                        />
                                        <div className="flex flex-col gap-0.5 min-w-0">
                                            <p className="text-sm font-medium truncate">
                                                {dateLabel} — {first.type}
                                            </p>
                                            <p className="text-sm text-default-500 truncate">
                                                {detail}
                                            </p>
                                        </div>
                                    </CardBody>
                                </Card>
                            );
                        })}
                    </div>
                </ModalBody>
                <ModalFooter className="flex flex-row items-center">
                    <div className="text-sm text-warning-500">
                        File processing may take a few minutes and will be completed in the background.
                    </div>
                    <Button
                        color="primary"
                        isDisabled={operationsImportPayload.length === 0}
                        onPress={() => importOperationMutation.mutate({
                            url: `field_connections/${connection?.id}/operations/`,
                            method: "post",
                            data: {
                                platform: connection.user_connection.platform,
                                operations: operationsImportPayload,
                            },
                        })}
                        isLoading={importOperationMutation.isPending}
                        className="flex-1"
                    >
                        Import
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ImportOperation;
